import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import i18n from 'i18n-js';
import { IconButton, ListItemIcon, Paper, Toolbar, Typography, Button } from '@material-ui/core';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import clsx from 'clsx';

import { MenuEditModal } from '../Modals/MenuEditModal';
import { BasicThreeDotsMenu, Text } from 'components/shared';
import { MenuSectionsTable } from '../MenuSectionManagement';
import logo from 'assets/breakfast-logo.svg';
import { useStyles } from './styles';
import { MenuType, Positions } from 'constants/enums';
import menuDetailsStore from 'store/menuDetailsStore';
import { ApplicationState } from 'store';
import { default as menusStore } from 'store/menusStore';
import { DishData, SectionData } from 'store/menuDetailsStore/reducer';
import {
    AddSectionModal,
    AddDailyMenuSectionModal,
    ChooseExistingModal,
    MenuIngredientManagement,
    ChooseExistingFromCatalogModal,
} from './components';
import { DishManagementDialog } from '../MenuSectionManagement/components';
import DailyMenuSectionManagementModal from './components/DailyMenuSectionManagementModal';

interface MenuDetailsProps {
    close?: () => void;
    isModal?: boolean;
}

export default function MenuManagementTable(props: MenuDetailsProps) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const params: { id: string } = useParams();
    const dishList = useSelector((state: ApplicationState) => state.menus.dishList);
    const user = useSelector((state: ApplicationState) => state.user);
    const menu = useSelector((state: ApplicationState) => state.menuDetails.menu);
    const [showAddCourseModal, setShowAddCourseModal] = useState(false);
    const [showAddSectionModal, setShowAddSectionModal] = useState(false);
    const [showChooseExistingModal, setShowChooseExistingModal] = useState(false);
    const [showChooseExistingFromCatalogModal, setShowChooseExistingFromCatalogModal] =
        useState(false);
    const [showEditMenuModal, setShowEditMenuModal] = useState(false);
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [toolbarMenuName, setToolbarMenuName] = useState('');
    const [dishToCopy, setDishToCopy] = useState<DishData | null>(null);

    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const isCatalogManager =
        menu.type === MenuType.Catalog &&
        menu.catalogData?.allowToEditCatalog &&
        currentRestaurant?.currentPosition === Positions.MANAGER;

    const menuType = menu.type;

    const [renderSections, setRenderSections] = useState<SectionData[]>([]);

    const draggedSection = useRef<SectionData | null>(null);
    const [draggedSectionId, setDraggedSectionId] = useState('');

    function onDragStart(section: SectionData) {
        setDraggedSectionId(section.id);
        draggedSection.current = section;
    }

    function onDragEnter(section: SectionData) {
        if (section.id !== draggedSection?.current?.id) {
            const tempSections = [...renderSections];
            const removedIndex = tempSections.indexOf(draggedSection?.current!);
            if (removedIndex < 0) {
                return;
            }
            const removedMenu = tempSections.splice(removedIndex, 1);
            const targetMenuIndex = tempSections.indexOf(section);
            if (removedIndex === targetMenuIndex) {
                if (removedIndex === 0 && targetMenuIndex === 0) {
                    tempSections.splice(targetMenuIndex + 1, 0, removedMenu[0]);
                } else if (removedIndex === 1 && targetMenuIndex === 0) {
                    tempSections.unshift(removedMenu[0]);
                } else {
                    tempSections.splice(targetMenuIndex + 1, 0, removedMenu[0]);
                }
            } else if (removedIndex > targetMenuIndex) {
                tempSections.splice(targetMenuIndex, 0, removedMenu[0]);
            } else {
                tempSections.splice(targetMenuIndex + 1, 0, removedMenu[0]);
            }

            setRenderSections(tempSections);
        }
    }

    function onDragEnd() {
        if (
            menu.sections.indexOf(draggedSection.current!) !==
            renderSections.indexOf(draggedSection.current!)
        ) {
            dispatch(
                menuDetailsStore.actionCreators.reorderSections(renderSections.map((x) => x.id))
            );
        }
        setDraggedSectionId('');
        draggedSection.current = null;
    }

    function addSection(name: string, price: number) {
        setShowAddSectionModal(false);
        dispatch(
            menuDetailsStore.actionCreators.addSection({
                name: name,
                price: price,
                menuId: menu.id,
            })
        );
    }

    function addExistingDish(sectionId: string, dishId: string, dish: DishData) {
        hideExistingModal();
        dispatch(
            menuDetailsStore.actionCreators.addExistingDish({
                sectionId,
                dishId,
                dish,
            })
        );
    }

    function showExistingModal(sectionId: string) {
        setSelectedSectionId(sectionId);
        setShowChooseExistingModal(true);
    }

    function showExistingFromCatalogModal(sectionId: string) {
        setSelectedSectionId(sectionId);
        setShowChooseExistingFromCatalogModal(true);
    }

    function hideExistingModal() {
        setShowChooseExistingModal(false);
        setSelectedSectionId('');
    }

    function handleBackButtonClick() {
        !!props.close ? props.close() : history.goBack();
    }

    useEffect(() => {
        dispatch(menusStore.actionCreators.getDishList());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (menu.id === params.id) {
            setRenderSections([...menu.sections]);
            setToolbarMenuName(menu.name);
        }
    }, [menu.id, menu.sections.length]);

    function getTotalItemAmount() {
        let total = 0;
        menu.sections.forEach((each) => {
            each.dishes.filter((dish) => !dish.isDisabled).forEach(() => (total += 1));
        });
        return total;
    }

    function getThreeDotItems() {
        const addSectionItem = (
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => setShowAddSectionModal(true)}
            >
                <ListItemIcon>
                    <AddIcon fontSize="small" color="disabled" />
                </ListItemIcon>
                <Typography variant="inherit">{i18n.t('common.addSection')}</Typography>
            </div>
        );
        const addCourseSectionItem = (
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => setShowAddCourseModal(true)}
            >
                <ListItemIcon>
                    <AddIcon fontSize="small" color="disabled" />
                </ListItemIcon>
                <Typography variant="inherit">{i18n.t('common.newCourse')}</Typography>
            </div>
        );
        const editItem = (
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => {
                    setShowEditMenuModal(true);
                }}
            >
                <ListItemIcon>
                    <BorderColorIcon fontSize="small" color="disabled" />
                </ListItemIcon>
                <Typography variant="inherit">{i18n.t('common.edit')}</Typography>
            </div>
        );
        switch (menu.type) {
            case MenuType.Standard:
            case MenuType.Special:
                return [addSectionItem, editItem];
            case MenuType.MultiCourse:
                return [addCourseSectionItem, editItem];
            case MenuType.Catalog:
                return isCatalogManager ? [addSectionItem, editItem] : [];
            default:
                return [editItem];
        }
    }

    return (
        <div className={classes.root}>
            {!props.isModal && toolbarMenuName && (
                <Toolbar style={{ paddingBottom: '10px' }}>
                    <IconButton
                        aria-label="delete"
                        size="small"
                        className={classes.iconButton}
                        onClick={handleBackButtonClick}
                    >
                        <ArrowBackIcon fontSize="inherit" />
                    </IconButton>
                    <Typography
                        variant="h6"
                        id="tableTitle"
                        component="div"
                        className={classes.toolbarTitle}
                    >
                        <Text text={toolbarMenuName} maxLength={40} fontSize={18} />
                        <img src={logo} className={classes.breakfastLogo} alt="logo" />
                        {menuType === MenuType.Daily && (
                            <span className={classes.dailyMenu}>
                                / {i18n.t('restaurant.dailyMenu')}
                            </span>
                        )}
                        {menuType === MenuType.MultiCourse && (
                            <span className={classes.dailyMenu}>
                                / {i18n.t('restaurant.multiCourseMenu')}
                            </span>
                        )}
                    </Typography>
                    {menuType === MenuType.Daily && <></>}
                    {menuType !== MenuType.Daily && (
                        <Typography align={'right'} className={classes.itemAmount}>
                            {`${getTotalItemAmount() || 0} ${i18n.t('common.items')}`}
                        </Typography>
                    )}
                    {currentRestaurant?.currentPosition === Positions.MANAGER &&
                        (menuType === MenuType.Daily ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowAddSectionModal(true)}
                            >
                                {`+ ${i18n.t('restaurant.newMenu').toUpperCase()}`}
                            </Button>
                        ) : (
                            <BasicThreeDotsMenu items={getThreeDotItems()} />
                        ))}
                </Toolbar>
            )}

            <Paper
                className={clsx({
                    [classes.forModal]: props.isModal,
                    [classes.menuSectionsContainer]: props.isModal,
                })}
            >
                {props.isModal ? (
                    <>
                        <div style={{ width: '67%' }}>
                            {menu.sections.length > 0 &&
                                menu.sections.map((section: any) => (
                                    <MenuSectionsTable
                                        key={section.name}
                                        onChooseExisting={() => showExistingModal(section.id)}
                                        onChooseFromCatalog={() =>
                                            showExistingFromCatalogModal(section.id)
                                        }
                                        onDishEdit={section.onDishEdit}
                                        sectionId={section.id}
                                        isDisabled={section.isDisabled}
                                        isModal={props.isModal}
                                    />
                                ))}
                        </div>
                        <div style={{ width: '30%' }}>
                            <MenuIngredientManagement forNewOrderItems />
                        </div>
                    </>
                ) : (
                    renderSections.length > 0 &&
                    renderSections.map((section: any) => (
                        <div
                            key={section.id}
                            draggable={currentRestaurant?.currentPosition === Positions.MANAGER}
                            onDragStart={() => {
                                onDragStart(section);
                            }}
                            onDragEnter={() => onDragEnter(section)}
                            onDragEnd={onDragEnd}
                            onDragOver={(e) => e.preventDefault()}
                            style={{
                                opacity: section.id === draggedSectionId ? 0.1 : 1,
                            }}
                        >
                            <MenuSectionsTable
                                key={section.name}
                                onChooseExisting={() => showExistingModal(section.id)}
                                onChooseFromCatalog={() => showExistingFromCatalogModal(section.id)}
                                onDishEdit={section.onDishEdit}
                                sectionId={section.id}
                                isDisabled={section.isDisabled}
                                isModal={props.isModal}
                            />
                        </div>
                    ))
                )}
            </Paper>
            <AddSectionModal
                isVisible={showAddSectionModal && menu.type !== MenuType.Daily}
                onClose={() => setShowAddSectionModal(false)}
                onAdd={(name, price) => addSection(name, price)}
                sections={menu.sections.map((x) => x.name.toUpperCase())}
                menuType={menu.type}
            />
            <AddDailyMenuSectionModal
                isOpen={showAddSectionModal && menu.type === MenuType.Daily}
                onClose={() => setShowAddSectionModal(false)}
                menuType={menu.type}
            />
            <ChooseExistingModal
                dishList={dishList}
                sections={menu.sections}
                isVisible={showChooseExistingModal}
                onClose={() => setShowChooseExistingModal(false)}
                onAdd={(sectionId, dishId, dish) => addExistingDish(sectionId, dishId, dish)}
                sectionId={selectedSectionId}
                menuType={menuType}
            />
            <ChooseExistingFromCatalogModal
                isVisible={showChooseExistingFromCatalogModal}
                onClose={() => setShowChooseExistingFromCatalogModal(false)}
                sectionId={selectedSectionId}
                onSubmit={(dish) => setDishToCopy(dish)}
            />
            <MenuEditModal
                menu={{
                    ...menu,
                    edit: null,
                    id: menu.id,
                    name: menu.name,
                    description: menu.description,
                    availableFrom: menu.availableFrom,
                    availableTo: menu.availableTo,
                    price: menu.price,
                    photo: menu.photo,
                    type: menu.type,
                    sections: [],
                    dishesCount: menu.dishesCount,
                    isEnabled: menu.isEnabled,
                    position: menu.position,
                    discount: menu.discount,
                }}
                isOpen={showEditMenuModal}
                handleClose={() => setShowEditMenuModal(false)}
                isDetails={true}
            />
            <DailyMenuSectionManagementModal
                onClose={() => setShowAddCourseModal(false)}
                isOpen={showAddCourseModal}
                menuType={menu.type}
            />
            {dishToCopy && (
                <DishManagementDialog
                    dish={{ ...dishToCopy, id: '', catalogData: menu.catalogData }}
                    sections={menu.sections}
                    action={(model) =>
                        dispatch(menuDetailsStore.actionCreators.copyDishFromCatalog(model))
                    }
                    isOpen={dishToCopy !== null}
                    onClose={() => setDishToCopy(null)}
                    disabled={false}
                    forCopy={false}
                />
            )}
        </div>
    );
}
